import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'ik_admin_token_v1';

const fmtErr = (d) => {
  if (d == null) return 'Something went wrong. Please try again.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (typeof d.msg === 'string') return d.msg;
  return String(d);
};

const Stat = ({ value, label, testid }) => (
  <div className="ik-stat">
    <div className="ik-stat-val" data-testid={testid}>{value}</div>
    <div className="ik-stat-key">{label}</div>
  </div>
);

const IntakeAdmin = ({ token, onSignOut }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/admin/responses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) {
        if (res.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          onSignOut();
          return;
        }
        throw new Error(fmtErr(body.detail));
      }
      setData(body);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onSignOut]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.responses?.length) return;
    const cols = ['created_at', 'name', 'email', 'band_label', 'band_mid', 'q1', 'q2',
      'score', 'tier', 'core_pct', 'explore_pct', 'precommit_signed', 'precommit_name'];
    const rows = [cols.join(',')].concat(
      data.responses.map((r) =>
        cols.map((c) => `"${String(r[c] ?? '').replace(/"/g, '""')}"`).join(',')),
    );
    const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `intake-responses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="intake-admin-panel">
      <div className="ik-admin-bar">
        <div className="ik-eyebrow">Member placement records</div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="button" className="ik-btn-link" onClick={exportCsv}
            data-testid="intake-admin-export">Export CSV</button>
          <button type="button" className="ik-btn-link" onClick={onSignOut}
            data-testid="intake-admin-signout">Sign out</button>
        </div>
      </div>

      {error && <div className="ik-error" data-testid="intake-admin-error">{error}</div>}

      {data && (
        <>
          <div className="ik-admin-stats">
            <Stat value={data.total} label="Responses" testid="intake-admin-total" />
            <Stat value={data.tier_counts['1']} label="Tier 1 · 100/0" testid="intake-admin-tier1" />
            <Stat value={data.tier_counts['2']} label="Tier 2 · 70/30" testid="intake-admin-tier2" />
            <Stat value={data.tier_counts['3']} label="Tier 3 · 40/60" testid="intake-admin-tier3" />
          </div>

          <div className="ik-table-wrap">
            {data.responses.length === 0 ? (
              <div className="ik-empty" data-testid="intake-admin-empty">
                No responses yet.
              </div>
            ) : (
              <table className="ik-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Name</th><th>Email</th><th>Allocation</th>
                    <th>Tier</th><th>Core / Explore</th><th>Magnitude</th>
                    <th>Grind</th><th>Signed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.responses.map((r, i) => (
                    <tr key={i} data-testid={`intake-admin-row-${i}`}>
                      <td>{(r.created_at || '').slice(0, 10)}</td>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td>{r.band_label}</td>
                      <td><span className={`ik-pill ik-pill-${r.tier}`}>Tier {r.tier}</span></td>
                      <td>{r.core_pct} / {r.explore_pct}</td>
                      <td>{r.q1} / 4</td>
                      <td>{r.q2} / 4</td>
                      <td>
                        <span className={`ik-pill ${r.precommit_signed ? 'ik-pill-yes' : 'ik-pill-no'}`}>
                          {r.precommit_signed ? 'Yes' : 'Declined'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {data.declined_precommitment > 0 && (
            <div className="ik-footnote" data-testid="intake-admin-declined-note">
              {data.declined_precommitment} member
              {data.declined_precommitment === 1 ? '' : 's'} declined the pre-commitment and
              were stepped down one tier.
            </div>
          )}
        </>
      )}

      {loading && !data && <div className="ik-empty">Loading records…</div>}
    </div>
  );
};

export default IntakeAdmin;
export { TOKEN_KEY, fmtErr };
